import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { api } from '../services/api';

const FavoritesContext = createContext();

const initialState = {
  favorites: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 0,
  hasMore: true
};

const favoritesReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_FAVORITES':
      return {
        ...state,
        favorites: action.payload.favorites,
        totalCount: action.payload.total_count,
        loading: false,
        error: null
      };
    
    case 'ADD_FAVORITE':
      return {
        ...state,
        favorites: [action.payload, ...state.favorites],
        totalCount: state.totalCount + 1,
        loading: false,
        error: null
      };
    
    case 'UPDATE_FAVORITE':
      return {
        ...state,
        favorites: state.favorites.map(fav => 
          fav.id === action.payload.id ? action.payload : fav
        ),
        loading: false,
        error: null
      };
    
    case 'DELETE_FAVORITE':
      return {
        ...state,
        favorites: state.favorites.filter(fav => fav.id !== action.payload),
        totalCount: state.totalCount - 1,
        loading: false,
        error: null
      };
    
    case 'APPEND_FAVORITES':
      return {
        ...state,
        favorites: [...state.favorites, ...action.payload.favorites],
        totalCount: action.payload.total_count,
        currentPage: state.currentPage + 1,
        hasMore: action.payload.favorites.length > 0,
        loading: false,
        error: null
      };
    
    case 'RESET_FAVORITES':
      return {
        ...initialState
      };
    
    default:
      return state;
  }
};

export const FavoritesProvider = ({ children }) => {
  const [state, dispatch] = useReducer(favoritesReducer, initialState);

  // Get all favorites
  const getFavorites = useCallback(async (skip = 0, limit = 100) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const data = await api.getFavorites(skip, limit);
      if (skip === 0) {
        dispatch({ type: 'SET_FAVORITES', payload: data });
      } else {
        dispatch({ type: 'APPEND_FAVORITES', payload: data });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Create a new favorite
  const createFavorite = useCallback(async (favoriteData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const newFavorite = await api.createFavorite(favoriteData);
      dispatch({ type: 'ADD_FAVORITE', payload: newFavorite });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Update a favorite
  const updateFavorite = useCallback(async (favoriteId, favoriteData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const updatedFavorite = await api.updateFavorite(favoriteId, favoriteData);
      dispatch({ type: 'UPDATE_FAVORITE', payload: updatedFavorite });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Delete a favorite
  const deleteFavorite = useCallback(async (favoriteId) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      await api.deleteFavorite(favoriteId);
      dispatch({ type: 'DELETE_FAVORITE', payload: favoriteId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Check if a favorite exists
  const checkFavoriteExists = useCallback(async (countyId, parcelIds) => {
    try {
      const result = await api.checkFavoriteExists(countyId, parcelIds);
      return result;
    } catch (error) {
      console.error('Check favorite exists error:', error);
      return { exists: false };
    }
  }, []);

  // Get favorites nearby
  const getFavoritesNearby = useCallback(async (latitude, longitude, radiusMiles = 10.0) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const data = await api.getFavoritesNearby(latitude, longitude, radiusMiles);
      dispatch({ type: 'SET_FAVORITES', payload: data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Load more favorites (for pagination)
  const loadMoreFavorites = useCallback(async () => {
    if (state.loading || !state.hasMore) return;
    
    const skip = state.favorites.length;
    await getFavorites(skip, 100);
  }, [state.loading, state.hasMore, state.favorites.length, getFavorites]);

  // Reset favorites
  const resetFavorites = useCallback(() => {
    dispatch({ type: 'RESET_FAVORITES' });
  }, []);

  const value = {
    ...state,
    getFavorites,
    createFavorite,
    updateFavorite,
    deleteFavorite,
    checkFavoriteExists,
    getFavoritesNearby,
    loadMoreFavorites,
    resetFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}; 